import React, { useRef } from "react";
import { Eye, X, ArrowLeft, ArrowRight } from "lucide-react";

const SubmitsDescription: React.FC<{ text: string }> = ({ text }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const limit = 80;

  if (text.length <= limit) {
    return <p className="text-white/80 text-sm leading-snug">{text}</p>;
  }

  return (
    <p className="text-white/80 text-sm font-regular leading-snug">
      {isExpanded ? text : `${text.substring(0, limit)}...`}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="ml-1 text-primary hover:text-primary-light font-bold text-[10px] uppercase tracking-wider focus:outline-none"
      >
        {isExpanded ? "Close" : "Read More"}
      </button>
    </p>
  );
};

const SubmitsCard: React.FC<{ activity: { title: string; date: string; desc: string; img: string } }> = ({ activity }) => {
  const [isVisible, setIsVisible] = React.useState(true);

  return (
    <div className="relative w-[320px] h-[400px] rounded-2xl overflow-hidden border border-border-subtle dark:border-white/10 group cursor-pointer">
      <img
        alt={activity.title}
        width="320"
        height="400"
        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
        src={activity.img}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
      
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="absolute top-4 right-4 z-20 size-8 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all animate-in fade-in zoom-in duration-300"
          title="Show details"
          aria-label="Show details"
        >
          <Eye className="w-4.5 h-4.5" />
        </button>
      )}

      <div 
        className={`absolute bottom-4 left-4 right-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg min-h-[160px] flex flex-col transition-all duration-500 ease-in-out ${
          isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        }`}
      >
        <div className="flex justify-between items-start mb-2 h-10 overflow-hidden">
          <h3 className="font-medium text-white text-sm leading-tight line-clamp-2">
            {activity.title}
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsVisible(false);
            }}
            className="flex-shrink-0 ml-2 size-6 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            title="Hide details"
            aria-label="Hide details"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <SubmitsDescription text={activity.desc} />
          <div className="mt-2 text-[10px] text-white/60 uppercase tracking-widest font-bold">
            {activity.date}
          </div>
        </div>
      </div>
    </div>
  );
};

const EventsHighlightsSection: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activities, setActivities] = React.useState<any[]>([]);

  React.useEffect(() => {
    fetch("/api/submits")
      .then((res) => res.json())
      .then((data) => {
        const mapped = data.map((item: any) => ({
          title: item.title,
          date: item.date,
          desc: item.description,
          img: item.image,
        }));
        setActivities(mapped);
      })
      .catch((err) => console.warn("Failed to load submits:", err));
  }, []);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 340; // Card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section>
      <div className="flex justify-between items-end mb-8">
        <div>
          <span className="text-primary font-bold tracking-widest text-xs uppercase mb-2 block">
            Recent Activities
          </span>
          <h2 className="font-tight text-3xl font-bold text-text-main dark:text-white">
            Events & Highlights
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => scroll("left")}
            className="size-10 rounded-full border border-border-subtle dark:border-white/10 bg-surface dark:bg-surface-dark hover:bg-canvas dark:hover:bg-white/5 flex items-center justify-center text-text-main dark:text-white transition-colors"
            aria-label="Scroll left"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scroll("right")}
            className="size-10 rounded-full border border-border-subtle dark:border-white/10 bg-surface dark:bg-surface-dark hover:bg-canvas dark:hover:bg-white/5 flex items-center justify-center text-text-main dark:text-white transition-colors"
            aria-label="Scroll right"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto no-scrollbar pb-4 -mx-4 px-4 sm:mx-0 sm:px-0"
      >
        <div className="flex gap-6 w-max">
          {activities.length === 0 ? (
            <p className="text-sm text-text-sub dark:text-gray-400 py-8">No recent activities available.</p>
          ) : (
            activities.map((activity, index) => (
              <SubmitsCard key={index} activity={activity} />
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default EventsHighlightsSection;
